import { Link, useRouter } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { MAIN_CONTENT_ID } from "~/components/a11y/skip-link";
import { useT } from "~/i18n";
import { type DestinationIconName, NavIcon } from "./nav-icon";
import { OperationTraceHelp, type OperationTraceStep } from "./operation-trace";
import { type PageAccent, pageAccentFor } from "./page-accent";
import { type RulesSection, RulesWorkspaceNav } from "./rules-workspace-nav";

/**
 * How wide the page measure may grow on large screens. Pick it per route
 * (docs/design-system.md, "Page width"):
 * - `reading` (default): 1,440px line for home, account, forms and docs.
 * - `wide`: data pages (tables, split list/detail, sweeps) grow fluidly to
 *   1,920px so extra columns and panes can appear.
 * - `full`: the live agent screen, up to 2,560px.
 * Below 1,440px of content all three render identically.
 */
export type PageWidth = "reading" | "wide" | "full";

export type Breadcrumb = {
  label: string;
  to?: string;
  search?: Record<string, string | number | boolean | undefined>;
};

/**
 * Every route renders exactly one `<Page>`, which owns the `<main>` landmark,
 * the single `<h1>`, and the post-navigation focus move.
 *
 * The header is compact on purpose: breadcrumb (detail pages only), the
 * destination's icon chip in its domain hue (destination pages only), a 24px
 * title with an optional context pill ("Conjunto em vigor"), one line of
 * description, at most one primary action on the right, then the tabs. The
 * origin → state → next trace is opt-in behind "Como funciona". The domain
 * (`data-accent`) retargets `--accent-*` for everything inside the page.
 */
export function Page({
  title,
  description,
  breadcrumbs,
  actions,
  trace,
  rulesSection,
  toolsNav,
  context,
  className,
  width = "reading",
  accent,
  icon,
  children,
}: {
  title: ReactNode;
  /**
   * @deprecated The header no longer renders a destination icon box. Kept so
   * existing routes compile; the rail already shows the destination icon.
   */
  mark?: ReactNode;
  /**
   * @deprecated The header no longer renders an eyebrow above the title. The
   * rail and the breadcrumb name the section.
   */
  eyebrow?: ReactNode;
  description?: ReactNode;
  breadcrumbs?: Breadcrumb[];
  actions?: ReactNode;
  /** Origin → state → next. Rendered behind a "Como funciona" disclosure. */
  trace?: OperationTraceStep;
  rulesSection?: RulesSection;
  toolsNav?: ReactNode;
  /** Small fact beside the title, e.g. the ruleset in force. */
  context?: ReactNode;
  className?: string;
  /** Maximum measure on large screens. */
  width?: PageWidth;
  /** Domain hue; derived from the path when omitted (see page-accent.ts). */
  accent?: PageAccent;
  /** Header icon chip; derived from the path when omitted. */
  icon?: DestinationIconName | null;
  children: ReactNode;
}) {
  const t = useT();
  // Read at render: every route renders its own Page, so the location is
  // already the matched one. Unit tests may render a Page without a router.
  const router = useRouter({ warn: false }) as ReturnType<typeof useRouter> | null;
  const derived = pageAccentFor(router?.state.location.pathname ?? "");
  const pageAccent = accent ?? derived.accent;
  // Detail pages lead with a breadcrumb; the chip marks destinations only.
  const pageIcon =
    breadcrumbs && breadcrumbs.length > 0 ? null : icon === undefined ? derived.icon : icon;

  return (
    <main
      className={`app-main scroll-thin${className ? ` ${className}` : ""}`}
      id={MAIN_CONTENT_ID}
      data-width={width}
      data-accent={pageAccent}
      // biome-ignore lint/a11y/noNoninteractiveTabindex: the main landmark is the app's keyboard-scrollable region.
      tabIndex={0}
      aria-label={t("a11y.mainLandmark")}
    >
      <div className="app-main__measure">
        {breadcrumbs && breadcrumbs.length > 0 && (
          <nav aria-label={t("a11y.breadcrumb")}>
            <ol className="breadcrumbs">
              {breadcrumbs.map((crumb) => (
                <li key={`${crumb.label}-${crumb.to ?? ""}`}>
                  {crumb.to ? (
                    <Link to={crumb.to} search={crumb.search}>
                      {crumb.label}
                    </Link>
                  ) : (
                    crumb.label
                  )}
                </li>
              ))}
            </ol>
          </nav>
        )}

        <div className="page-header">
          <div className="page-header__text">
            <div className="page-header__title-row">
              {pageIcon ? (
                <span className="page-header__icon" aria-hidden="true">
                  <NavIcon name={pageIcon} />
                </span>
              ) : null}
              <h1>{title}</h1>
              {context ? <div className="page-header__context">{context}</div> : null}
              {trace ? <OperationTraceHelp {...trace} /> : null}
            </div>
            {description && <p className="page-header__description">{description}</p>}
          </div>
          {actions && <div className="page-header__actions">{actions}</div>}
        </div>

        {rulesSection && <RulesWorkspaceNav active={rulesSection} />}
        {toolsNav}

        {children}
      </div>
    </main>
  );
}
